import {
  signInWithEmailAndPassword, 
  sendPasswordResetEmail, 
  signOut, 
  signInWithRedirect, 
  signInWithPopup, 
  createUserWithEmailAndPassword,
  OAuthProvider,
  getAuth
} from 'firebase/auth';
import { getDatabase, ref, get, set, update } from 'firebase/database';
import { db, auth, googleAuthProvider, facebookAuthProvider } from 'auth/FirebaseAuth';
import logError from '../utils/logger';
import debug from '../utils/debugLog';


const FirebaseService = {
  signInEmailRequest: async (email, password) => 
    signInWithEmailAndPassword(auth, email, password)
      .then(user => user)
      .catch(err => err),

  forgetPassword: async (email) => 
    sendPasswordResetEmail(auth, email)
      .then(user => user)
      .catch(err => err),

  signOutRequest: async () => 
    signOut(auth)
      .then(user => user)
      .catch(err => err),

  signInOpenIdRequest: async (provider) => {
    try {
      const result = await signInWithPopup(auth, provider);
      // User is signed in.
      // IdP data available using getAdditionalUserInfo(result)
      // Get the OAuth access token and ID Token
      const credential = OAuthProvider.credentialFromResult(result);
      const accessToken = credential.accessToken;
      const idToken = credential.idToken;
      return result;
    } catch (error) {
      // Handle error.
      //Enviar error a log
      logError(error);
      console.error("Error during OpenID sign-in:", error);
      return { message: error.message, code: error.code };
    }
  },

  signInGoogleRequest: async () => 
    signInWithPopup(auth, googleAuthProvider)
      .then(user => user)
      .catch(err => err),

  signInFacebookRequest: async () => 
    signInWithPopup(auth, facebookAuthProvider)
      .then(user => user)
      .catch(err => err),

  signUpEmailRequest: async (email, password) => 
    createUserWithEmailAndPassword(auth, email, password)
      .then(user => user)
      .catch(err => err),

  // Llamadas a DB
  getPrimaryUserData: async (uid) => {
    try {
      // logError(uid)
      const snap = await get(ref(db, `Clients/${uid}`));
      if (snap.exists()) {
        return snap;
      } else {
        throw new Error('No data available');
      }
    } catch (err) {
      logError(err);
      throw err;
    }
  },

  getDisabledProjectInfo: async (projectId) => {
    try {
      debug.log('[DisabledCheck] Querying:', `Config/DisabledProjects/${projectId}`);
      const snap = await get(ref(db, `Config/DisabledProjects/${projectId}`));
      debug.log('[DisabledCheck] snap.exists():', snap.exists(), 'val:', snap.val());
      if (snap.exists()) {
        return snap.val();
      }
      return null;
    } catch (err) {
      console.error('[DisabledCheck] Error:', err);
      logError(err);
      return null;
    }
  },

  getSecondaryUserData: (secondary, uid) => 
    get(ref(getDatabase(secondary), `Usuarios/${uid}/Submenu`))
      .then(snap => snap)
      .catch(err => err),

  checkEmailInSecondaryDB: async (uid, email, secondary, primaryProfile) => {
    try {
      const secondaryDb = getDatabase(secondary);
      const pickNonEmpty = (...values) => values.find(v => v !== undefined && v !== null && v !== '');

      const primaryLtiData = primaryProfile ? {
        curso: primaryProfile.Curso,
        seccion: primaryProfile.Seccion || primaryProfile.SeccionSap,
        sede: primaryProfile.Sede,
        nombre: primaryProfile.Nombre,
        role: primaryProfile.Rol,
        email: primaryProfile.Email || email
      } : null;
      
      // Obtener datos de LTI desde los claims del token del usuario autenticado
      let ltiData = null;
      // contextValid: el backend marca labelValid=false cuando context.label no tiene
      // el formato esperado. En ese caso forzamos Curso='None' para que la plataforma
      // no muestre ningún curso. Por defecto true (no afecta logins no-LTI).
      let contextValid = true;
      try {
        const currentUser = auth.currentUser;
        if (currentUser && currentUser.uid === uid) {
          // Force refresh so we read the latest custom claims after LTI login.
          const idTokenResult = await currentUser.getIdTokenResult(true);
          const claims = idTokenResult.claims;
          debug.log('[FirebaseService] Token claims:', claims);
          if (claims.labelValid === false) contextValid = false;
          if (claims.lti) {
            ltiData = {
              curso: claims.curso,
              seccion: claims.seccion,
              sede: claims.sede ?? claims.Sede,
              nombre: claims.nombre || '',
              role: claims.role || '',
              email: claims.email || email
            };
            debug.log('[FirebaseService] LTI data from token claims:', ltiData);
          }
        } else if (currentUser && currentUser.uid !== uid) {
          debug.warn('[FirebaseService] Skipping auth.currentUser claims due to uid mismatch:', { expectedUid: uid, authUid: currentUser.uid });
        }
      } catch (err) {
        debug.log('[FirebaseService] Error reading token claims:', err);
      }

      // Para campos de contexto LTI (curso, seccion, sede), los claims del token
      // tienen prioridad sobre la Primary DB, ya que los claims provienen directamente
      // del launch LTI y la lectura de Primary DB puede devolver datos cacheados/viejos.
      const canonicalLtiData = {
        curso: pickNonEmpty(ltiData?.curso, primaryLtiData?.curso),
        seccion: pickNonEmpty(ltiData?.seccion, primaryLtiData?.seccion),
        sede: pickNonEmpty(ltiData?.sede, primaryLtiData?.sede),
        nombre: pickNonEmpty(primaryLtiData?.nombre, ltiData?.nombre),
        role: pickNonEmpty(primaryLtiData?.role, ltiData?.role),
        email: pickNonEmpty(primaryLtiData?.email, ltiData?.email, email)
      };

      const canonicalSede = canonicalLtiData.sede;

      // Verificar si el usuario ya existe en la colección Usuarios
      const usuariosRef = ref(secondaryDb, `Usuarios/${uid}`);
      const usuariosSnap = await get(usuariosRef);

      if (usuariosSnap.exists()) {
        // Usuario ya existe: actualizar campos LTI disponibles, incluyendo Sede aunque no vengan curso/seccion.
        if (canonicalLtiData) {
          const updatePayload = {};
          // Contexto inválido → Curso='None' (la plataforma no mostrará ningún curso).
          if (!contextValid) {
            updatePayload.Curso = 'None';
          } else if (canonicalLtiData.curso) {
            updatePayload.Curso = canonicalLtiData.curso;
          }
          if (canonicalLtiData.seccion) updatePayload.Seccion = canonicalLtiData.seccion;
          if (canonicalLtiData.nombre) updatePayload.Nombre = canonicalLtiData.nombre;
          if (canonicalLtiData.role) updatePayload.Rol = canonicalLtiData.role;
          if (canonicalSede !== undefined && canonicalSede !== null) updatePayload.Sede = canonicalSede;

          if (Object.keys(updatePayload).length > 0) {
            debug.log('[FirebaseService] Updating existing user with LTI fields:', updatePayload);
            await update(usuariosRef, updatePayload);
          }
        }
        return true;
      }

      // Usuario no existe: crearlo
      {
        // Si hay datos de LTI, crear usuario directamente con esos datos
        if (canonicalLtiData.curso && canonicalLtiData.seccion) {
          debug.log('[FirebaseService] Creating user from LTI data');
          const plantillaRef = ref(secondaryDb, `AdmisionPlantilla`);
          const plantillaSnap = await get(plantillaRef);
          const plantillaData = plantillaSnap.exists() ? plantillaSnap.val() : {};

          const nuevoUsuario = {
            ...plantillaData,
            Curso: canonicalLtiData.curso,
            Email: canonicalLtiData.email || `${uid}@noemail.local`,
            Nombre: canonicalLtiData.nombre || `Usuario ${uid}`,
            Seccion: canonicalLtiData.seccion,
            Sede: canonicalSede ?? plantillaData.Sede ?? '',
            Rol: canonicalLtiData.role || plantillaData.Rol || 'Alumno',
            role: canonicalLtiData.role || plantillaData.Rol || 'Alumno',
            user: canonicalLtiData.email || `${uid}@noemail.local`,
            uid: uid,
            TimestampInicio: Date.now(),
            TimestampFinal: Date.now() + 30 * 24 * 60 * 60 * 1000
          };

          await set(usuariosRef, nuevoUsuario);
          debug.log('[FirebaseService] User created from LTI:', nuevoUsuario);
          return true;
        }

        // Si no hay datos LTI completos, buscar en Admision
        const isFakeEmail = !email || email.includes('@fake.lti') || email.includes('@noemail.local');
        let admisionSnap = null;
        
        if (!isFakeEmail) {
          const cleanedEmail = email.replace(/[.#$[\]]/g, '');
          const admisionRef = ref(secondaryDb, `Admision/${cleanedEmail}`);
          admisionSnap = await get(admisionRef);
        }

        // Si existe admision, usarla
        if (admisionSnap && admisionSnap.exists()) {
          const admisionData = admisionSnap.val();
          const plantillaRef = ref(secondaryDb, `AdmisionPlantilla`);
          const plantillaSnap = await get(plantillaRef);
          const plantillaData = plantillaSnap.exists() ? plantillaSnap.val() : {};

          const nuevoUsuario = {
            ...plantillaData,
            Curso: admisionData.curso || plantillaData.Curso || 'AIA1101',
            Email: admisionData.email ?? email ?? `${uid}@noemail.local`,
            Nombre: admisionData.nombre || plantillaData.Nombre || `Usuario ${uid}`,
            Seccion: admisionData.seccion || plantillaData.Seccion || '',
            Sede: canonicalSede ?? admisionData.sede ?? plantillaData.Sede ?? '',
            Rol: canonicalLtiData.role || admisionData.rol || plantillaData.Rol || 'Alumno',
            role: canonicalLtiData.role || admisionData.rol || plantillaData.Rol || 'Alumno',
            user: admisionData.email ?? email ?? `${uid}@noemail.local`,
            uid: uid,
            TimestampInicio: Date.now(),
            TimestampFinal: Date.now() + 30 * 24 * 60 * 60 * 1000
          };

          await set(usuariosRef, nuevoUsuario);
        } else {
          // No existe admision ni LTI completo: intentar plantilla o crear registro mínimo
          const plantillaRef = ref(secondaryDb, `AdmisionPlantilla`);
          const plantillaSnap = await get(plantillaRef);
          if (plantillaSnap.exists()) {
            const plantillaData = plantillaSnap.val();
            const nuevoUsuario = {
              ...plantillaData,
              Curso: canonicalLtiData.curso || plantillaData.Curso || 'AIA1101',
              Email: email || `${uid}@noemail.local`,
              Nombre: canonicalLtiData.nombre || plantillaData.Nombre || `Usuario ${uid}`,
              Seccion: canonicalLtiData.seccion || plantillaData.Seccion || '',
              Sede: canonicalSede ?? plantillaData.Sede ?? '',
              Rol: canonicalLtiData.role || plantillaData.Rol || 'Alumno',
              role: canonicalLtiData.role || plantillaData.Rol || 'Alumno',
              uid,
              TimestampInicio: Date.now(),
              TimestampFinal: Date.now() + 30 * 24 * 60 * 60 * 1000
            };
            await set(usuariosRef, nuevoUsuario);
          } else {
            // Plantilla tampoco existe: crear registro mínimo
            const nuevoUsuario = {
              uid,
              Email: email || `${uid}@noemail.local`,
              Nombre: canonicalLtiData.nombre || `Usuario ${uid}`,
              Curso: canonicalLtiData.curso || 'AIA1101',
              Seccion: canonicalLtiData.seccion || '',
              Sede: canonicalSede ?? '',
              Rol: canonicalLtiData.role || 'Alumno',
              role: canonicalLtiData.role || 'Alumno',
              TimestampInicio: Date.now(),
              TimestampFinal: Date.now() + 30 * 24 * 60 * 60 * 1000
            };
            await set(usuariosRef, nuevoUsuario);
          }
        }
      }

      // Contexto inválido → forzar Curso='None' en cualquier rama de creación,
      // para que la plataforma no muestre ningún curso al usuario.
      if (!contextValid) {
        await update(usuariosRef, { Curso: 'None' });
      }

      return true; // Usuario creado o ya existente
    } catch (err) {
      logError(err);
      throw err;
    }
  }
}

export default FirebaseService;
